import { Button, Stack } from '@mui/material';
import {
  Body,
  Subheading
} from '@natomalabs/natomui/src/components/Typography';
import HomeViewPaper from './HomeViewPaper';
import { useEffect, useState, useMemo } from 'react';
import { RedirectType, redirect } from 'next/navigation';
import {
  useGetMyOrgsQuery,
  useRegisterMutation,
  type LoginInfo
} from '@/__generated__/graphql';
import useEnvironment from '../providers/Environment';
import TextField from './TextField';
import { useSessionContext } from '../providers/Session';

function RegistrationForm({ loginInfo }: { loginInfo?: LoginInfo }) {
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const { natoma } = useEnvironment();

  const email = loginInfo?.email;
  const [name, setName] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [register, { data, error }] = useRegisterMutation({
    variables: {
      displayName: name,
      orgDisplayName,
      registrationCode
    }
  });

  // console.log('LoginInfo', loginInfo);
  useEffect(() => {
    if (!data || error) return;
    if (!data?.register?.currentOrg?.slug) return;
    if (loginInfo?.ssoOrg) {
      redirect(
        `https://${data.register.currentOrg.slug}.${natoma.rootDomain}/logout`
      );
    } else {
      redirect(
        `https://${data.register.currentOrg.slug}.${natoma.rootDomain}`,
        RedirectType.push
      );
    }
  }, [data, error, loginInfo?.ssoOrg, natoma]);

  let errorMessage = null;
  if (error?.graphQLErrors) {
    errorMessage = <Body color="error">{error.graphQLErrors[0].message}</Body>;
  }
  return (
    <>
      <TextField
        placeholder="Email"
        label="Email"
        value={email ?? ''}
        disabled
      />
      <TextField
        placeholder="Name"
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextField
        placeholder="My Company"
        label="Organization Name"
        value={orgDisplayName}
        data-testid={testIds.companyNameInput}
        onChange={(e) => setOrgDisplayName(e.target.value)}
      />
      <TextField
        placeholder="Registration code"
        label="Registration Code"
        value={registrationCode}
        data-testid={testIds.registrationCodeInput}
        onChange={(e) => setRegistrationCode(e.target.value)}
      />
      {errorMessage}
      <Button
        variant="primary"
        onClick={() => register()}
        disabled={!orgDisplayName || !registrationCode}
        data-testid={testIds.registerButton}
      >
        Create organization
      </Button>
    </>
  );
}

export default function RegistrationPage() {
  const { loginInfo } = useSessionContext();
  const { natoma, auth } = useEnvironment();

  // If tenant registration is disabled, redirect to main page
  if (auth.disableTenantRegistration) {
    redirect(`https://${natoma.rootDomain}`);
  }

  // Fetch the set of orgs for the logged in user - this is used to determine if
  // we show the interstitial state to hint new users into joining existing orgs
  const { loading: isLoadingOrgs, data: orgData } = useGetMyOrgsQuery();
  const [confirmOrgCreation, _setConfirmOrgCreation] = useState(false);

  // We show the interstitial when this user has no other orgs and hasn't
  // confirmed they want to create a new org. This has the effect that when
  // we arrive at this page via the org picker, we won't prompt the user
  // for yet another confirmation before the registration flow
  const showInterstitial = useMemo(
    () => !isLoadingOrgs && orgData?.myOrgs.length === 0 && !confirmOrgCreation,
    [isLoadingOrgs, orgData, confirmOrgCreation]
  );

  // The queries that drive this page will by and large already have started
  // fetching, this component will have an almost imperceptible loading time.
  if (isLoadingOrgs) return null;

  return (
    <HomeViewPaper>
      <Stack spacing={4}>
        {showInterstitial ? (
          <>
            <Subheading>Welcome to Natoma</Subheading>
            <Body>
              It looks like this might be your first sign-in. If your team
              already uses Natoma, please reach out to your Natoma admins and
              join via an invitation.
              <br />
              <br />
              You may also create a new organization.
            </Body>

            <Button
              variant="primary"
              onClick={() => _setConfirmOrgCreation(true)}
            >
              Create a new organization
            </Button>
          </>
        ) : (
          <>
            <Subheading>Create a new organization</Subheading>
            <Body>
              Complete your registration with a few details about yourself and
              your organization
            </Body>
            <RegistrationForm loginInfo={loginInfo} />
          </>
        )}
      </Stack>
    </HomeViewPaper>
  );
}

export const testIds = {
  registerButton: 'RegistrationPage-registerButton',
  companyNameInput: 'RegistrationPage-companyNameInput',
  registrationCodeInput: 'RegistrationPage-registrationCodeInput'
};
